import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const ENGAGEMENTS = [
  {
    id: "01",
    title: "Business Central — Full Implementations",
    category: "Implementation",
    scope:
      "End-to-end rollouts across Sales, Purchase, Warehouse, Production, Project and Gate Entry modules — from requirement gathering through go-live and hyper-care.",
    stack: ["AL", "Business Central", "Cloud", "On-Premises"],
  },
  {
    id: "02",
    title: "Custom Purchase Requisition Flow",
    category: "Customisation",
    scope:
      "Designed and built a bespoke Purchase Requisition process on top of Business Central — approval matrices, budget checks and hand-offs into standard Purchase Orders.",
    stack: ["AL Extensions", "Approval Workflow"],
  },
  {
    id: "03",
    title: "Quality Control Workflow",
    category: "Customisation",
    scope:
      "Custom Quality Control process for inbound goods and production output — inspection templates, pass/fail gating, and integration with Warehouse & Production journals.",
    stack: ["AL Extensions", "Warehouse", "Production"],
  },
  {
    id: "04",
    title: "Navision → Business Central Upgrade",
    category: "Migration",
    scope:
      "Object-level upgrade from legacy Navision to Business Central — converting C/AL objects into clean AL extensions without disrupting live operations.",
    stack: ["C/AL", "AL", "Object Migration"],
  },
  {
    id: "05",
    title: "Business Central Integrations",
    category: "Integration",
    scope:
      "Integrated Business Central with Azure, banking, accounting and CRM systems for automated, cross-system data flow. Additional connectors for Power Apps, eshopaid and Outlook.",
    stack: ["REST API", "Azure", "Power Platform", "Postman"],
  },
  {
    id: "06",
    title: "RDLC & Excel Reports Suite",
    category: "Reporting",
    scope:
      "Built and customised 30+ multi-level RDLC and Excel reports for Sales, Purchase, Finance and Manufacturing — grouped exactly how the operating teams needed them.",
    stack: ["RDLC", "Excel", "Reporting"],
  },
  {
    id: "07",
    title: "Multi-Project Support",
    category: "Support",
    scope:
      "Ongoing support across 6+ concurrent Business Central customers — fixes, minor customisations and feature enhancements spanning finance, sales, purchase and manufacturing.",
    stack: ["AL", "L2 / L3 Support"],
  },
  {
    id: "08",
    title: "Expense Manager",
    category: "Power Apps · Internal Tool",
    scope:
      "Internal expense management app built end-to-end in Power Apps. Role-based approvals for managers and admins, advance-amount handling, and review workflows triggered from inside the app or from Outlook & Teams.",
    stack: ["Power Apps", "Power Automate", "SharePoint"],
  },
  {
    id: "09",
    title: "OAuth 2.0 API Connector",
    category: "Open Source · Integration",
    scope:
      "AL extension that connects Business Central to external APIs over OAuth 2.0, so API data can be pulled in and checked inside BC. Tested end to end with Postman and documented with a step-by-step setup guide.",
    stack: ["AL", "OAuth 2.0", "REST API", "Postman"],
    link: "https://github.com/aryakadulkar/Auth-2-API-data",
  },
  {
    id: "10",
    title: "BC Easy Reporting",
    category: "Open Source · Reporting",
    scope:
      "Build Excel reports and charts from Business Central data without writing a new report object every time. Includes a dynamic report builder and BC graphs.",
    stack: ["AL", "Excel", "Charts"],
    link: "https://github.com/aryakadulkar/BC-Easy-reporting",
  },
  {
    id: "11",
    title: "BC Management Reports Pack",
    category: "Open Source · Reporting",
    scope:
      "Management reports covering every Business Central module, shipped with a report guide and a field-mapping sheet so teams know exactly where each number comes from.",
    stack: ["AL", "RDLC", "Excel"],
    link: "https://github.com/aryakadulkar/BC-Management-Reports",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 15 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.05 },
  }),
};

export default function ProProjects() {
  return (
    <div className="pt-24 pb-16" data-testid="pro-projects">
      <section className="max-w-6xl mx-auto px-5 md:px-8">
        <p className="font-plexMono text-xs uppercase tracking-[0.2em] text-terracotta">
          Selected work
        </p>
        <h1 className="font-serif font-light text-4xl md:text-6xl text-neutral-900 mt-3">
          What I&apos;ve worked on.
        </h1>
        <p className="font-plex text-neutral-600 mt-4 max-w-2xl">
          Highlights from Business Central implementations, upgrades, integrations and Power
          Platform projects delivered across clients over the last three years.
        </p>

        <div className="mt-14 border-t border-neutral-200">
          {ENGAGEMENTS.map((e, i) => (
            <motion.article
              key={e.id}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className="group grid md:grid-cols-12 gap-6 md:gap-10 py-8 border-b border-neutral-200 hover:bg-white transition-colors px-2 -mx-2 rounded-lg"
              data-testid={`pro-engagement-${e.id}`}
            >
              <div className="md:col-span-2">
                <p className="font-plexMono text-xs text-neutral-400">— {e.id}</p>
                <p className="font-plexMono text-xs uppercase tracking-widest text-terracotta mt-2">
                  {e.category}
                </p>
              </div>
              <div className="md:col-span-10">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="font-serif text-2xl md:text-3xl text-neutral-900 leading-tight group-hover:text-terracotta transition-colors">
                    {e.title}
                  </h2>
                  <ArrowRight
                    size={20}
                    className="shrink-0 text-neutral-300 group-hover:text-terracotta group-hover:translate-x-1 transition-all mt-2"
                  />
                </div>
                <p className="font-plex text-neutral-600 mt-3 leading-relaxed max-w-3xl">
                  {e.scope}
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {e.stack.map((s) => (
                    <span
                      key={s}
                      className="font-plexMono text-[11px] px-2.5 py-1 border border-neutral-200 rounded-full text-neutral-600"
                    >
                      {s}
                    </span>
                  ))}
                </div>
                {e.link && (
                  <a
                    href={e.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block font-plexMono text-xs uppercase tracking-widest text-terracotta mt-4 hover:underline"
                    data-testid={`pro-engagement-${e.id}-link`}
                  >
                    View on GitHub →
                  </a>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
